/**
 * Glass-pill filter control for the merchant catalogue, shown in the global
 * UnifiedAppHeader trailing cluster when a merchant rate library is open.
 *
 * Rendered via the id-intercept in `renderLeadingAction` despite living
 * trailing — the trailing cluster routes a short allowlist of ids through that
 * function precisely so pills with bespoke rendering survive the move.
 *
 * Category and brand can't be flat `subActions` — a TP-sized catalogue has
 * dozens of categories and the brand list is capped at 100, which would make
 * HeaderPopoverPill's unscrolled menu unusable. So this pill opens a small
 * panel holding two native selects instead, keeping the per-option counts
 * ("DIY (3,703)") that make the choice informative.
 *
 * The panel stays open after a pick so category and brand can be set in one
 * visit. Uses the same `PopoverAnchor virtualRef` pattern as
 * BOQMoveSelectionPill to sidestep the shared popover wrapper's
 * `Children.map` interaction with Radix's `Slot.Children.only`.
 */
import { useRef, useState } from 'react';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '../../../shared/ui/popover';
import type { MobileHeaderAction } from './types';
import type { ResourceLibraryMerchantHeaderState } from './resourceLibrary';

const setCategory = (category: string) =>
  window.dispatchEvent(new CustomEvent('resource-library:set-category', { detail: { category } }));

const setBrand = (brand: string) =>
  window.dispatchEvent(new CustomEvent('resource-library:set-brand', { detail: { brand } }));

export function MerchantFilterPill({
  action,
  state,
}: {
  action: MobileHeaderAction;
  state: ResourceLibraryMerchantHeaderState;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const Icon = action.icon;
  const badge = action.badge ?? 0;

  const handleReset = () => {
    window.dispatchEvent(new CustomEvent('resource-library:reset-filters'));
    setOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={action.ariaLabel}
        data-no-window-drag="true"
        onClick={() => setOpen((prev) => !prev)}
        className="relative flex items-center justify-center active:opacity-75 transition-opacity"
        style={{
          height: 44,
          padding: '0 2px',
          borderRadius: 296,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          pointerEvents: 'auto',
        }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none"
          style={{
            position: 'absolute',
            // 36 fill inside the 44 hit area, insetting by the button's own 2px
            // padding so it hugs the 36 content span. Must match
            // renderMobileHeaderAction — this pill sits directly beside
            // resource-library-merchant-sort, which renders through that path,
            // so a 44 fill here reads as one pill bigger than its neighbour.
            left: 2,
            right: 2,
            height: 36,
            top: 'calc(50% - 18px)',
            zIndex: 0,
            borderRadius: 296,
            background: 'var(--header-pill-fill)',
            mixBlendMode: 'var(--header-pill-blend)' as any,
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.08), var(--glass-inset, none)',
          }}
        />
        <span
          className="relative flex items-center justify-center"
          style={{ minWidth: 36, height: 36, zIndex: 1, color: 'var(--foreground)' }}
        >
          {Icon && <Icon style={{ width: 20, height: 20 }} strokeWidth={2} />}
        </span>
        {badge > 0 && (
          <span
            className="pointer-events-none absolute top-0 right-0 min-w-[16px] h-4 rounded-full text-[9px] font-bold flex items-center justify-center px-1"
            style={{ background: 'var(--destructive)', color: 'var(--destructive-foreground)', zIndex: 2 }}
          >
            {badge}
          </span>
        )}
      </button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor virtualRef={buttonRef as any} />
        <PopoverContent surface="solid" align="start" sideOffset={6} className="w-[280px] p-3 space-y-3">
          <div
            className="font-apple uppercase tracking-wider"
            style={{ fontSize: '11px', color: 'var(--muted-foreground)' }}
          >
            Filter {state.merchantName}
          </div>

          <label className="block space-y-1">
            <span className="block text-xs text-muted-foreground">Category</span>
            <select
              value={state.activeCategory}
              onChange={(e) => setCategory(e.target.value)}
              className="h-8 w-full text-sm rounded-md border border-border bg-background px-2"
            >
              <option value="all">All categories</option>
              {state.categories.map((c) => (
                <option key={c.name} value={c.name}>{c.name} ({c.n.toLocaleString()})</option>
              ))}
            </select>
          </label>

          <label className="block space-y-1">
            <span className="block text-xs text-muted-foreground">Brand</span>
            <select
              value={state.activeBrand}
              onChange={(e) => setBrand(e.target.value)}
              className="h-8 w-full text-sm rounded-md border border-border bg-background px-2"
            >
              <option value="all">All brands</option>
              {state.brands.map((b) => (
                <option key={b.name} value={b.name}>{b.name} ({b.n.toLocaleString()})</option>
              ))}
            </select>
            {/* The tail of one-off brands is cut to keep the list usable; free
                text search still reaches them, so nothing is unreachable. */}
            <span className="block text-[11px] text-muted-foreground/70">
              Top {state.brands.length} brands. Search to reach the rest.
            </span>
          </label>

          {/* Always enabled: it also clears the search query, which lives in
              the header and isn't visible to this panel. */}
          <button
            type="button"
            onClick={handleReset}
            className="w-full h-8 text-sm rounded-md border border-border text-foreground hover:bg-muted transition-colors"
          >
            Reset filters
          </button>
        </PopoverContent>
      </Popover>
    </>
  );
}
